import { useState, type FormEvent } from 'react';
import { patchMyProfile } from '../../api/accounts';
import { changePassword } from '../../api/auth';
import { roleLabelKey } from '../../api/roles';
import type { StrapiUser, UserRole } from '../../api/types';
import { useT } from '../../i18n/i18n.tsx';
import { Button, Dialog, InlineAlert, TextField } from '../../ui';
import { parseDisplayName, passwordErrorMessage, passwordFormError, PASSWORD_MIN } from './profile';
import '../auth/LoginDialog.css';
import './profile.css';
import { apiErrorMessage } from '../../api/errorMessage.ts';

/**
 * MVP-231: the one screen an account's owner has for their own account. Opened from the email in
 * the header, which is where a person already looks to find out who they are signed in as.
 *
 * Username, email and role are read-only on purpose (see the spec): email is the login identifier
 * and this Strapi has email confirmation revoked, so a self-service change would be one typo away
 * from an account nobody can recover. An admin changes those in the Strapi admin panel.
 *
 * The two forms submit separately and report separately — saving a display name and changing a
 * password are unrelated acts, and one failing must not throw away the other's work.
 */
export function ProfileDialog({
  open,
  onClose,
  user,
  role,
}: {
  open: boolean;
  onClose: () => void;
  user: StrapiUser;
  role: UserRole | null;
}) {
  const t = useT();
  const [displayName, setDisplayName] = useState(user.displayName ?? '');
  const [nameState, setNameState] = useState<{ saving: boolean; error: string | null; saved: boolean }>({
    saving: false,
    error: null,
    saved: false,
  });

  const [fields, setFields] = useState({ current: '', next: '', confirm: '' });
  const [pwState, setPwState] = useState<{ saving: boolean; error: string | null; saved: boolean }>({
    saving: false,
    error: null,
    saved: false,
  });

  async function handleName(event: FormEvent) {
    event.preventDefault();
    if (nameState.saving) return;
    const parsed = parseDisplayName(displayName);
    if (!parsed.ok) {
      setNameState({ saving: false, error: t(parsed.error.key, parsed.error.params), saved: false });
      return;
    }
    setNameState({ saving: true, error: null, saved: false });
    try {
      const updated = await patchMyProfile(parsed.value);
      setDisplayName(updated.displayName ?? '');
      setNameState({ saving: false, error: null, saved: true });
    } catch (err) {
      setNameState({
        saving: false,
        error: apiErrorMessage(err, t('account.saveFailed'), t),
        saved: false,
      });
    }
  }

  async function handlePassword(event: FormEvent) {
    event.preventDefault();
    if (pwState.saving) return;
    const problem = passwordFormError(fields);
    if (problem) {
      setPwState({ saving: false, error: t(problem.key, problem.params), saved: false });
      return;
    }
    setPwState({ saving: true, error: null, saved: false });
    try {
      await changePassword(fields.current, fields.next);
      // Cleared on success, always: these are the person's real passwords, and leaving them in a
      // dialog that stays mounted after it closes is three fields of plaintext waiting for the next
      // person at the machine.
      setFields({ current: '', next: '', confirm: '' });
      setPwState({ saving: false, error: null, saved: true });
    } catch (err) {
      setPwState({ saving: false, error: passwordErrorMessage(err, t), saved: false });
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={t('account.title')} closeLabel={t('common.close')}>
      <div className="profile-dialog">
        <dl className="profile-dialog__facts">
          <div>
            <dt className="text-meta">{t('account.username')}</dt>
            <dd className="text-body">{user.username}</dd>
          </div>
          <div>
            <dt className="text-meta">{t('account.email')}</dt>
            <dd className="text-body">{user.email}</dd>
          </div>
          <div>
            <dt className="text-meta">{t('account.role')}</dt>
            <dd className="text-body">{t(roleLabelKey(role))}</dd>
          </div>
        </dl>
        <p className="text-meta">{t('account.askAdmin')}</p>

        <form className="login-dialog__form" onSubmit={handleName}>
          <h3 className="profile-dialog__heading text-subtitle">{t('account.displayName')}</h3>
          {nameState.error && <InlineAlert variant="danger">{nameState.error}</InlineAlert>}
          {nameState.saved && <InlineAlert variant="ok">{t('account.displayNameSaved')}</InlineAlert>}
          <TextField
            label={t('account.displayName')}
            name="displayName"
            value={displayName}
            placeholder={user.username}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setNameState({ saving: false, error: null, saved: false });
            }}
          />
          <Button type="submit" variant="secondary" loading={nameState.saving}>
            {t('account.saveDisplayName')}
          </Button>
        </form>

        <form className="login-dialog__form" onSubmit={handlePassword}>
          <h3 className="profile-dialog__heading text-subtitle">{t('account.password')}</h3>
          {pwState.error && <InlineAlert variant="danger">{pwState.error}</InlineAlert>}
          {pwState.saved && <InlineAlert variant="ok">{t('account.passwordChanged')}</InlineAlert>}
          <TextField
            label={t('account.currentPassword')}
            name="currentPassword"
            type="password"
            autoComplete="current-password"
            value={fields.current}
            onChange={(e) => setFields((f) => ({ ...f, current: e.target.value }))}
          />
          <TextField
            label={t('account.newPassword')}
            name="newPassword"
            type="password"
            autoComplete="new-password"
            minLength={PASSWORD_MIN}
            placeholder={t('auth.passwordMinHint', { min: PASSWORD_MIN })}
            value={fields.next}
            onChange={(e) => setFields((f) => ({ ...f, next: e.target.value }))}
          />
          <TextField
            label={t('account.newPasswordAgain')}
            name="newPasswordConfirm"
            type="password"
            autoComplete="new-password"
            value={fields.confirm}
            onChange={(e) => setFields((f) => ({ ...f, confirm: e.target.value }))}
          />
          <Button type="submit" variant="secondary" loading={pwState.saving}>
            {t('account.changePassword')}
          </Button>
        </form>
      </div>
    </Dialog>
  );
}
